import React, { useState, useRef, useEffect } from 'react';
import { is, isNil, isEmpty, head } from 'ramda';
import { __ } from '@wordpress/i18n';

// components
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import CaretIcon from '../../icons/CaretIcon';

function DropdownWithSearch({
                                children,
                                options,
                                value,
                                placeholder = '',
                                searchValue,
                                searchFn,
                                onKeyDownEnterFn,
                                onKeyDownSearchEnterFn,
                                selectFn,
                                disableSearch = false,
                                optionLabel,
                                labelKey = 'label',
                                valueKey = 'value',
                                emptyLabel = __('Empty list', 'soundbetween'),
                                nonChosenLabel = __('Scegli', 'soundbetween'),
                                displayNonChosenLabel = false,
                                dataFieldId
                            }) {
    const refSearch = useRef(null);
    const refSelect = useRef(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const classesSelectEmpty = ['dropdownWithSearch', ...(!isEmpty(emptyLabel) ? ['dropdownWithSearch--empty'] : [])];
    const classesSelect = ['dropdownWithSearch', ...(showDropdown ? ['expanded'] : [])];

    const displayDropdown = () => {
        setShowDropdown(true);
    }

    const hideDropdown = () => {
        setShowDropdown(false);
    }

    const hideDropdownOnEsc = (e) => {
        e.stopPropagation();

        if (27 === e.keyCode) {
            setShowDropdown(false);
            if (refSelect && refSelect.current) {
                refSelect.current.focus();
            }
        }
    }

    const onKeyDownWrapper = (e) => {
        if (13 === e.keyCode) {
            if (!showDropdown) {
                setShowDropdown(true);
            }
        }
    }

    const onKeyDown = (e) => {
        if (13 === e.keyCode) {
            if (!isNil(onKeyDownEnterFn)) {
                onKeyDownEnterFn(e)
            }
            setShowDropdown(false);
        }
    }

    const onKeyDownSearch = (e) => {
        if (13 === e.keyCode) {
            if (!isNil(onKeyDownSearchEnterFn)) {
                onKeyDownSearchEnterFn(e)
            }
            setShowDropdown(false);
        }
    }

    const onClickItem = (e) => {
        const { value } = { ...e.target.dataset };
        selectFn(value);
        setShowDropdown(false);
    }

    const properLabel = (value, fallback = undefined) => {
        const searchFor = value ? value : fallback;
        const find = head(options
            .reduce((acc, cur) => {
                acc = [...acc, ...(cur.children ? cur.children : [cur])];
                return acc;
            }, [])
            .filter(o => String(o[valueKey]) === String(searchFor)));

        return value && value[labelKey]
            ? value[labelKey]
            : find && find[labelKey]
                ? find[labelKey]
                : !isEmpty(value) && value ? value : fallback;
    }

    useEffect(() => {
        if (!showDropdown && searchFn) {
            searchFn('');
        }
        if (showDropdown && refSearch.current) {
            refSearch.current.focus();
        }
        const handleClickOutside = (e) => {
            if (refSelect.current && !refSelect.current.contains(e.target)) {
                setShowDropdown(false);
            }
        };

        if (showDropdown) {
            window.addEventListener('keydown', hideDropdownOnEsc, false);
            window.addEventListener('click', handleClickOutside);
        }
        return () => {
            window.removeEventListener('keydown', hideDropdownOnEsc, false);
            window.removeEventListener('click', handleClickOutside);
        };
    }, [showDropdown]);

    return (
        isNil(options)
            ? <div ref={refSelect} className={classesSelectEmpty.join(' ')}>
                <div className="dropdownWithSearch__valueWrapper">
                    <span className="dropdownWithSearch__value">
                        {!isEmpty(emptyLabel) ? emptyLabel : placeholder}
                    </span>
                </div>
            </div>
            : <div ref={refSelect} className={classesSelect.join(' ')} tabIndex={0} onKeyDown={onKeyDownWrapper}>
                <div className="dropdownWithSearch__valueWrapper" onClick={displayDropdown}>
                    {children}
                    <span className={`dropdownWithSearch__value${(!value || isEmpty(value)) ? ' inherited' : ''}`}>
                        {displayNonChosenLabel ? nonChosenLabel : properLabel(value)}
                    </span>
                    <input
                        value={properLabel(value)}
                        data-field-id={dataFieldId}
                        placeholder={placeholder}
                        onChange={e => selectFn(e.target.value)}
                        autoComplete="off"
                        className="dropdownWithSearch__hiddenField"/>

                </div>
                <div className="dropdownWithSearch__caretIcon" onClick={displayDropdown}>
                    <CaretIcon/>
                </div>
                {showDropdown
                    ? <div className="dropdownWithSearch__dropdown">
                        <div className="dropdownWithSearch__basicWrapper">
                            {children}
                            <span>{optionLabel ? optionLabel : placeholder}</span>
                            <div className="dropdownWithSearch__crossIcon" onClick={hideDropdown}>
                                <CaretIcon/>
                            </div>
                        </div>
                        {!disableSearch && searchFn
                            ? <div className="dropdownWithSearch__searchWrapper">
                                <input
                                    value={searchValue}
                                    ref={refSearch}
                                    onChange={e => searchFn(e.target.value)}
                                    onKeyDown={onKeyDownSearch}
                                    autoComplete="off"
                                    className="dropdownWithSearch__search"/>
                            </div>
                            : null}
                        <OverlayScrollbarsComponent
                            element="div"
                            options={{ scrollbars: { autoHide: 'never' } }}
                            defer>
                            <div className="dropdownWithSearch__resultsWrapper">
                                {!isEmpty(options)
                                    ? options.map(o => !isNil(o.children) && is(Array, o.children)
                                        ? <div key={o[valueKey]}>
                                            {o[labelKey] ?
                                                <span className="dropdownWithSearch__cat">{o[labelKey]}</span> : null}
                                            {o.children.map(ob => <button
                                                className="dropdownWithSearch__item"
                                                key={ob[valueKey]}
                                                data-value={ob[valueKey]}
                                                onKeyDown={onKeyDown}
                                                onClick={onClickItem}>{ob[labelKey]}</button>)}
                                        </div>
                                        : o[valueKey] === 'no_matches'
                                            ? <div key={o[valueKey]}>
                                            <span className="dropdownWithSearch__noMatches">
                                                {o[labelKey] ? o[labelKey] : o[valueKey]}
                                            </span>
                                            </div>
                                            : <button className="dropdownWithSearch__item"
                                                      key={o[valueKey]}
                                                      data-value={o[valueKey]}
                                                      onKeyDown={onKeyDown}
                                                      onClick={onClickItem}>
                                                {o[labelKey] ? o[labelKey] : o[valueKey]}
                                            </button>)
                                    : null}
                            </div>
                        </OverlayScrollbarsComponent>
                    </div>
                    : null}
            </div>
    );
}

export default DropdownWithSearch;
